import React from 'react';
import ReactDOM from 'react-dom/client';
import * as Sentry from '@sentry/react';
import { HelmetProvider } from 'react-helmet-async';
import App from './App';
import './index.css';
import { I18nextProvider } from 'react-i18next';
import i18n from './i18n/index';
import { I18nLoadingWrapper } from './i18n/I18nLoadingWrapper';

// CRITICAL FIX: Suppress AbortError unhandled rejections globally
// These occur during React StrictMode when Firestore listeners are cancelled
// before they complete initialization. They're safe to ignore but cause
// UI error states if left as unhandled rejections.
window.addEventListener('unhandledrejection', (event) => {
  const reason = event.reason;
  if (
    reason instanceof Error &&
    (reason.name === 'AbortError' || reason.message?.includes('aborted'))
  ) {
    // Prevent the error from propagating and triggering error UI
    event.preventDefault();
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.debug('[main] Suppressed AbortError during listener cleanup');
    }
  }
});

// Validate critical imports before rendering
if (!HelmetProvider) {
  console.error('❌ CRITICAL: HelmetProvider is undefined!');
  throw new Error('HelmetProvider failed to import from react-helmet-async');
}

if (!i18n) {
  console.error('❌ CRITICAL: i18n is undefined!');
  throw new Error('i18n failed to import from ./i18n/index');
}

// Initialize Sentry error tracking
if (import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.MODE || 'development',
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    // Performance Monitoring
    tracesSampleRate: import.meta.env.MODE === 'production' ? 0.1 : 1.0, // 10% in prod, 100% in dev

    // Session Replay
    replaysSessionSampleRate: 0.1, // 10% of sessions
    replaysOnErrorSampleRate: 0.1, // 10% of errors — avoid exhausting 50-replay quota

    // Filter out sensitive data
    beforeSend(event, _hint) {
      // Drop AbortErrors — these are noisy cancel events (React Query unmount,
      // Firestore listener cleanup) and account for ~99% of our Sentry error quota.
      // They are already suppressed via window.onunhandledrejection but Sentry
      // can still capture them via its own instrumentation.
      const exception = event.exception?.values?.[0];
      if (
        exception?.type === 'AbortError' ||
        exception?.value?.includes('AbortError') ||
        exception?.value?.includes('user aborted a request') ||
        exception?.value?.includes('signal is aborted')
      ) {
        return null;
      }

      // Remove sensitive headers
      if (event.request?.headers) {
        delete event.request.headers['Authorization'];
        delete event.request.headers['Cookie'];
        delete event.request.headers['X-Auth-Token'];
      }

      // Remove sensitive query params
      if (event.request?.query_string) {
        const sensitiveParams = ['token', 'apiKey', 'password', 'secret'];
        const queryString = event.request.query_string;

        // Handle query_string as string or array of tuples
        if (typeof queryString === 'string') {
          let sanitized = queryString;
          sensitiveParams.forEach(param => {
            if (sanitized.includes(param)) {
              sanitized = sanitized.replace(
                new RegExp(`${param}=[^&]*`, 'gi'),
                `${param}=[REDACTED]`
              );
            }
          });
          event.request.query_string = sanitized;
        } else if (Array.isArray(queryString)) {
          // Handle as array of [key, value] tuples
          event.request.query_string = queryString.map(([key, value]) => {
            const lowerKey = key.toLowerCase();
            if (sensitiveParams.some(param => lowerKey.includes(param.toLowerCase()))) {
              return [key, '[REDACTED]'] as [string, string];
            }
            return [key, value] as [string, string];
          });
        }
      }

      return event;
    },
  });
}

/**
 * SECURITY: HTML escape function to prevent XSS in error messages.
 * Uses simple character replacement - no dependencies (works even if module loading fails).
 * This runs before React mounts, so we can't rely on DOMPurify being available.
 */
function escapeHtml(unsafe: string): string {
  return unsafe
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

// Wrap render in try-catch to catch import failures
try {
  const rootElement = document.getElementById('root');
  if (!rootElement) {
    throw new Error('Root element not found');
  }

  // Additional runtime validation (redundant but provides better error messages)
  if (typeof HelmetProvider !== 'function') {
    throw new Error(`HelmetProvider is not a function: ${typeof HelmetProvider}`);
  }

  const isE2E = import.meta.env.VITE_E2E === '1';

  const appTree = (
    <HelmetProvider>
      <I18nextProvider i18n={i18n}>
        <I18nLoadingWrapper>
          <App />
        </I18nLoadingWrapper>
      </I18nextProvider>
    </HelmetProvider>
  );

  // E2E stability: React 18 StrictMode intentionally double-mounts effects in dev.
  // Combined with Firestore emulator listeners, this can trigger rare internal assertion
  // crashes that break deterministic Playwright runs. We keep StrictMode for normal dev,
  // but disable it during Playwright E2E runs via VITE_E2E=1.
  ReactDOM.createRoot(rootElement).render(
    isE2E ? appTree : <React.StrictMode>{appTree}</React.StrictMode>
  );
} catch (error) {
  console.error('❌ CRITICAL: Failed to render app:', error);
  // SECURITY FIX: Escape HTML to prevent XSS via error message injection
  const safeMessage = escapeHtml(error instanceof Error ? error.message : String(error));
  document.getElementById('root')!.innerHTML = `
    <div style="padding: 20px; color: red;">
      <h1>Application Failed to Load</h1>
      <p><strong>Error:</strong> ${safeMessage}</p>
      <p>Check browser console for details.</p>
    </div>
  `;
  throw error;
}
